/* drl-product-showcase.js — in-view-only card video.
   Only loaded when a block actually carries a video (see the section).

   This observer owns when the clip plays and pauses; that has not changed.
   What it no longer owns is WHICH file plays. The <video> now ships with no
   src and preload="none", because Liquid's `video_tag` could only ever offer
   the 1080p master and a 300-500px tile was downloading all 11 MB of it. So
   play through window.drlVideo, which resolves the rendition off the HLS
   manifest, sets the src, and then plays.

   The clip still carries `muted playsinline`, which is the pair the autoplay
   policy looks at — a muted video is allowed to start from script, and the PDP
   gallery does exactly this. An earlier version of this file dropped `autoplay`
   and called play() while the element had no loadable source, which is the
   rejected promise that got blamed on the missing attribute. */

class DrlPsVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (window.drlVideo) window.drlVideo.play(this.video);
            else this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
        });
      },
      { rootMargin: '100px' }
    );
    // Observe the <video>, not the host: the host is `display: contents` so it
    // generates no box, and IntersectionObserver reports a box-less element as
    // never intersecting — the clip got paused on load and never resumed.
    this.observer.observe(this.video);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('drl-ps-video')) {
  customElements.define('drl-ps-video', DrlPsVideo);
}
